import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfDayInTz, endOfDayInTz, formatInTz, DEFAULT_TIMEZONE } from '@/utils/timezone';
import { calculateRateOrNull } from '@/utils/dateFieldStandards';
import { getComparisonPeriod } from '@/utils/periodComparison';
import { useAuth } from './useAuth';
import { useVisibility } from './useVisibility';
import type {
  CsrCohortLeadRow,
  CsrCohortReportData,
  CsrCohortStats,
  CsrLeaderboardRow,
  CsrLeadClass,
  FollowUpRecoveryMetrics,
  HourDayHeatmapCell,
  LossReasonBreakdown,
} from '@/types/reports';

/**
 * CSR cohort report.
 *
 * One RPC call (get_csr_lead_cohort) returns every lead created in the range,
 * already classified and attributed server-side. This hook only aggregates:
 * per-CSR leaderboard rows, totals, the two loss-reason charts, the follow-up
 * recovery block and the hour/day heatmap all come from the same rows, so the
 * expandable per-lead rows need no extra query.
 *
 * Range bounds always go through startOfDayInTz / endOfDayInTz with the
 * company timezone. Nothing here looks at jobs.job_date, closed_at or
 * scheduled_date.
 */

export const UNATTRIBUTED_KEY = 'unattributed';
export const UNATTRIBUTED_LABEL = 'Unattributed';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const GRADABLE_CLASSES: ReadonlySet<CsrLeadClass> = new Set(['booked', 'lost_csr', 'open']);

export const isGradableClass = (cls: CsrLeadClass) => GRADABLE_CLASSES.has(cls);

// Prefix → chart label. Mirrors the ILIKE prefixes in get_csr_lead_cohort and the
// labels emitted by CancelLeadDialog; anything else was typed under "Other".
const REASON_PREFIXES: Array<[prefix: string, label: string]> = [
  ['sales / vendor', 'Sales / vendor'],
  ['sales/vendor', 'Sales / vendor'],
  ['wrong number', 'Wrong number'],
  ['no answer', 'No answer / could not reach'],
  ['spam', 'Spam'],
  ['existing customer', 'Existing customer'],
  ['looking for shop', 'Looking for shop'],
  ['looking for parts', 'Looking for parts'],
  ['customer not ready', 'Customer not ready'],
  ['price too high', 'Price too high'],
  ['already serviced', 'Already serviced elsewhere'],
  ['issue resolved', 'Issue resolved itself'],
  ['out of service area', 'Out of service area'],
  ['out of area', 'Out of service area'],
  ['no tech available', 'No tech available'],
  ['out of scope', 'Out of scope'],
  ['commercial door', 'Commercial door'],
  ['gate repair', 'Gate repair'],
  ['test lead', 'Test lead'],
  ['duplicate', 'Duplicate'],
  ['data error', 'Data error'],
];
const OTHER_REASON_LABEL = 'Other (free text)';

export function normalizeLostReason(reason: string | null, status: string): string {
  const trimmed = (reason ?? '').trim();
  if (!trimmed) return status === 'spam' ? 'Spam' : 'Not specified';
  const lower = trimmed.toLowerCase();
  const match = REASON_PREFIXES.find(([prefix]) => lower.startsWith(prefix));
  return match ? match[1] : OTHER_REASON_LABEL;
}

interface MutableStats {
  cohort: number;
  excluded: number;
  lost_ops: number;
  booked: number;
  lost_csr: number;
  open: number;
  same_call_count: number;
  held_numerator: number;
  held_denominator: number;
  sla_met_count: number;
  sla_applicable_count: number;
}

const emptyStats = (): MutableStats => ({
  cohort: 0,
  excluded: 0,
  lost_ops: 0,
  booked: 0,
  lost_csr: 0,
  open: 0,
  same_call_count: 0,
  held_numerator: 0,
  held_denominator: 0,
  sla_met_count: 0,
  sla_applicable_count: 0,
});

function tally(stats: MutableStats, row: CsrCohortLeadRow) {
  stats.cohort++;
  switch (row.class) {
    case 'excluded': stats.excluded++; break;
    case 'lost_ops': stats.lost_ops++; break;
    case 'booked': stats.booked++; break;
    case 'lost_csr': stats.lost_csr++; break;
    case 'open': stats.open++; break;
  }
  if (row.class === 'booked') {
    if (row.same_call === true) stats.same_call_count++;
    if (row.held !== null) {
      stats.held_denominator++;
      if (row.held === true) stats.held_numerator++;
    }
  }
  if (row.sla_applicable) {
    stats.sla_applicable_count++;
    if (row.sla_met === true) stats.sla_met_count++;
  }
}

function finalize(stats: MutableStats): CsrCohortStats {
  const leads = stats.booked + stats.lost_csr + stats.open;
  return {
    ...stats,
    leads,
    book_rate: calculateRateOrNull(stats.booked, leads),
    same_call_rate: calculateRateOrNull(stats.same_call_count, stats.booked),
    held_rate: calculateRateOrNull(stats.held_numerator, stats.held_denominator),
    sla_rate: calculateRateOrNull(stats.sla_met_count, stats.sla_applicable_count),
  };
}

function toBreakdown(counts: Map<string, number>): LossReasonBreakdown[] {
  const total = Array.from(counts.values()).reduce((s, n) => s + n, 0);
  return Array.from(counts.entries())
    .map(([reason, count]) => ({
      reason,
      display_name: reason,
      count,
      percentage: total > 0 ? (count / total) * 100 : 0,
    }))
    .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));
}

/** Pure aggregation over RPC rows. Exported so it can be exercised without React. */
export function aggregateCsrCohort(rows: CsrCohortLeadRow[], timezone: string): CsrCohortReportData {
  const buckets = new Map<string, { csr_id: string | null; csr_name: string; stats: MutableStats; leadRows: CsrCohortLeadRow[] }>();
  const totals = emptyStats();
  const lostCounts = new Map<string, number>();
  const excludedCounts = new Map<string, number>();
  const heat = new Map<string, number>();
  let lostLeads = 0;
  let attempted = 0;
  let recovered = 0;

  for (const row of rows) {
    const key = row.csr_id ?? UNATTRIBUTED_KEY;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        csr_id: row.csr_id,
        csr_name: row.csr_id ? (row.csr_name || 'Unknown') : UNATTRIBUTED_LABEL,
        stats: emptyStats(),
        leadRows: [],
      };
      buckets.set(key, bucket);
    } else if (row.csr_id && row.csr_name && bucket.csr_name === 'Unknown') {
      bucket.csr_name = row.csr_name;
    }

    tally(bucket.stats, row);
    tally(totals, row);

    if (isGradableClass(row.class)) {
      bucket.leadRows.push(row);
      // ISO day (1 = Mon … 7 = Sun) → Sun = 0 to match HeatmapChart's day names.
      const day = Number(formatInTz(row.created_at, timezone, 'i')) % 7;
      const hour = Number(formatInTz(row.created_at, timezone, 'H'));
      const cellKey = `${day}-${hour}`;
      heat.set(cellKey, (heat.get(cellKey) ?? 0) + 1);
    }

    if (row.class === 'lost_csr') {
      const label = normalizeLostReason(row.lost_reason, row.status);
      lostCounts.set(label, (lostCounts.get(label) ?? 0) + 1);
    } else if (row.class === 'excluded') {
      const label = normalizeLostReason(row.lost_reason, row.status);
      excludedCounts.set(label, (excludedCounts.get(label) ?? 0) + 1);
    }

    if (row.ever_lost) {
      lostLeads++;
      if (row.followed_up_after_lost) attempted++;
      if (row.recovered) recovered++;
    }
  }

  const leaderboard: CsrLeaderboardRow[] = Array.from(buckets.values())
    .map(b => ({
      csr_id: b.csr_id,
      csr_name: b.csr_name,
      leadRows: b.leadRows.sort((a, z) => z.created_at.localeCompare(a.created_at)),
      ...finalize(b.stats),
    }))
    .sort((a, b) => {
      if (a.csr_id === null) return 1;
      if (b.csr_id === null) return -1;
      const ar = a.book_rate ?? -1;
      const br = b.book_rate ?? -1;
      if (br !== ar) return br - ar;
      if (b.leads !== a.leads) return b.leads - a.leads;
      return a.csr_name.localeCompare(b.csr_name);
    });

  const heatmap: HourDayHeatmapCell[] = Array.from(heat.entries()).map(([cellKey, count]) => {
    const [day, hour] = cellKey.split('-').map(Number);
    return { day, hour, dayName: DAY_NAMES[day], count, value: count };
  });

  const followUpRecovery: FollowUpRecoveryMetrics = {
    lost_leads: lostLeads,
    follow_up_attempted_count: attempted,
    follow_up_attempted_rate: calculateRateOrNull(attempted, lostLeads),
    recovered_count: recovered,
    recovered_rate: calculateRateOrNull(recovered, lostLeads),
  };

  return {
    rows,
    leaderboard,
    totals: finalize(totals),
    lostByCsr: toBreakdown(lostCounts),
    excludedReasons: toBreakdown(excludedCounts),
    followUpRecovery,
    heatmap,
  };
}

interface UseCsrCohortReportOptions {
  startDate: Date;
  endDate: Date;
  timezone?: string;
  enabled?: boolean;
}

export function useCsrCohortReport({ startDate, endDate, timezone = DEFAULT_TIMEZONE, enabled = true }: UseCsrCohortReportOptions) {
  const { user } = useAuth();
  const { isRestricted } = useVisibility();
  // Everyone but admins only sees themselves (UI scoping; RLS already lets them
  // read the company's leads, same rule the previous dashboard applied).
  const selfOnly = isRestricted;

  const startIso = startOfDayInTz(startDate, timezone).toISOString();
  const endIso = endOfDayInTz(endDate, timezone).toISOString();

  return useQuery({
    queryKey: ['reports', 'csr-cohort', startIso, endIso, timezone, selfOnly ? user?.id ?? 'self' : 'all'],
    queryFn: async (): Promise<CsrCohortReportData> => {
      const { data, error } = await supabase.rpc('get_csr_lead_cohort', {
        p_start: startIso,
        p_end: endIso,
      });
      if (error) throw error;
      const rows = (data ?? []) as unknown as CsrCohortLeadRow[];
      const scoped = selfOnly && user ? rows.filter(r => r.csr_id === user.id) : rows;
      return aggregateCsrCohort(scoped, timezone);
    },
    enabled: enabled && !!startDate && !!endDate,
    staleTime: 0,
  });
}

/**
 * Same report for the mirror-image previous period. Deferred: the container
 * passes enabled = main data loaded && compare toggle on, matching
 * useComparisonMetrics.
 */
export function useCsrCohortComparison(startDate: Date, endDate: Date, timezone: string, enabled: boolean) {
  const startMs = startDate.getTime();
  const endMs = endDate.getTime();
  const period = useMemo(() => getComparisonPeriod(new Date(startMs), new Date(endMs)), [startMs, endMs]);
  const query = useCsrCohortReport({ startDate: period.start, endDate: period.end, timezone, enabled });
  return { ...query, period };
}
